import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  filterUndefined,
  IFilter,
  ODataOperator,
  trackByRef,
  UrlPagedTable,
} from 'src/app/lego-utils';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DevelopmentNoveltyTableEditDrawer } from 'src/app/components/main-content/design-lookup/tables/development-novelty-table/development-novelty-table-edit/development-novelty-table-edit.drawer';
import { Task, TaskStatus } from 'src/app/models';
import { DesignService, PagedCollection, TaskService } from 'src/app/state';
import { TASK_PAGINATOR } from 'src/app/state/task/task-pagination.provider';

@Component({
  selector: 'em-development-novelty-table',
  styleUrls: ['./development-novelty-table.component.less'],
  templateUrl: './development-novelty-table.component.html',
  standalone: false,
})
export class DevelopmentNoveltyTableComponent implements OnInit, OnDestroy {
  public readonly taskStatus = TaskStatus;
  public readonly trackByRef = trackByRef;

  // A field rather than an observable: the template uses it twice, once inside
  // [nzNoResult] where an async pipe would re-subscribe per change detection.
  public errorMsg?: string;

  public readonly table: UrlPagedTable<Task>;
  public readonly routeParam;

  /** The design id is required before any request; see `UrlPagedTable`'s staticFilters$. */
  private readonly staticFilters$ = new BehaviorSubject<IFilter | undefined>(undefined);
  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly designService: DesignService,
    private readonly taskService: TaskService,
    private readonly drawerService: NzDrawerService,
    private readonly notification: NzNotificationService,
    route: ActivatedRoute,
    router: Router,
    @Inject(TASK_PAGINATOR) paginator: PagedCollection<Task>
  ) {
    this.table = new UrlPagedTable<Task>({
      drawer: {
        componentType: DevelopmentNoveltyTableEditDrawer,
        drawerService,
        getSingle: (id) => this.taskService.getSingle(id),
        name: 'Task',
        notification,
      },
      getPage: ({ page, size, sort, filters }) =>
        this.taskService.getPage(page, size, sort ? [sort] : undefined, filters),
      paginator,
      prefix: 'novelty_development_milestone',
      route,
      router,
      staticFilters$: this.staticFilters$.pipe(filterUndefined()),
    });

    this.routeParam = this.table.routeParam;
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.table.connect());

    this.subscriptions.add(
      this.taskService.error$.subscribe((errorMsg) => {
        this.errorMsg = errorMsg;
      })
    );

    this.subscriptions.add(
      this.designService
        .selectDesignId()
        .pipe(filterUndefined())
        .subscribe((designId) => {
          this.staticFilters$.next({
            key: 'projectId',
            operator: ODataOperator.Equals,
            value: designId,
          });
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public cancelMilestone(task: Task) {
    this.subscriptions.add(
      this.taskService.update(task.id, { status: TaskStatus.Canceled }).subscribe({
        error: (error: Error) => {
          this.notification.error('Error', error.message.split('§§§')[0]);
        },
        next: () => {
          this.notification.success(
            'Milestone cancelled',
            'The milestone was successfully cancelled'
          );

          this.table.refresh();
        },
      })
    );
  }
}
